<template>
    <div class="row" :class="{ 'is-prescription': isPrescription }">
        <div class="col-12 note-head">
            <div class="note-title">
                <h4>{{ note.name }}</h4>
            </div>
            <div class="note-btn d-flex align-items-center" v-if="!note.isHideAddBtn">
                <button @click="onOpenModal(note, index)" type="button" class="btn btn-outline-primary btn-sm">
                    <i v-if="!salesOrderId" class="fas fa-plus"></i>
                    <i v-else class="fas fa-pen"></i>
                </button>
            </div>
        </div>

        <div class="col-12"><hr class="mt-0"></div>
        <div class="col-12 note-body">
            <div class="table-container">
                <table class="table treatment-table mb-0" :class="{ 'is-compact': isPrescription }">
                    <thead>
                        <tr>
                            <th v-if="!isPrescription" class="col-action"></th>
                            <th class="col-item">Item Details</th>
                            <th v-if="!isPrescription" class="col-doctor">Doctor</th>
                            <th v-if="!isPrescription" class="col-schedule">Schedule</th>
                            <th class="col-dentition">Dentition Plan</th>
                            <th v-if="!isPrescription" class="col-bill-dent">Bill Dentition</th>
                            <th v-if="!isPrescription" class="col-amount">Bill Amount</th>
                            <th v-if="!isPrescription" class="col-teeth-amount">Net Amount</th>
                            <th v-if="!isPrescription" class="col-status">Status</th>
                        </tr>
                    </thead>

                    <tbody ref="tableRef">
                        <template v-for="(item, noteIndex) in note.selectedNotes" :key="noteIndex">
                            <tr :class="{ 'row-highlighted': generalId == item.id }">
                                <td v-if="!isPrescription" class="col-action">
                                    <button
                                        v-if="item.id"
                                        @click="onOpenTaskModal(item)"
                                        type="button"
                                        title="Task"
                                        class="btn btn-outline-primary btn-sm task-btn"
                                    >
                                        <i class="fas fa-plus"></i>
                                    </button>
                                    <button
                                        v-if="item.id"
                                        @click="onClickToRx(item)"
                                        type="button"
                                        title="Prescription"
                                        class="btn btn-outline-secondary btn-sm task-btn ms-1"
                                    >
                                        <i class="fas fa-prescription"></i>
                                    </button>
                                    <span v-if="!isPrescription">
                                      <button
                                          v-if="item.dentition_pertains && item.dentition_pertains.length"
                                          @click="onOpenBillDentition(item)"
                                          type="button"
                                          class="btn btn-sm tooth-btn"
                                      >
                                              <i class="fas fa-tooth"></i>
                                          </button>
                                    </span>
                                </td>
                                <td class="col-item fw-medium">
                                  {{ item.name }}
                                </td>
                                <td v-if="!isPrescription" class="col-doctor">{{ item.service_resource ? item.service_resource.name : '' }}</td>
                                <td v-if="!isPrescription" class="col-schedule text-nowrap">
                                    {{ item.schedule_date ? formatDate(new Date(item.schedule_date)) : '' }}
                                </td>
                                <td class="col-dentition" :title="item.dentition ? item.dentition.join(', ') : ''">{{ item.dentition ? item.dentition.join(', ') : '' }}</td>
                                <td v-if="!isPrescription" class="col-bill-dent">
                                    <div class="d-flex align-items-center justify-content-between">
                                        <span :title="getDentitionPertains(item.dentition_pertains)">{{ getDentitionPertains(item.dentition_pertains) }}</span>
                                    </div>
                                </td>
                                <td v-if="!isPrescription" class="col-amount text-end fw-medium">{{ item.total }}</td>
                                <td v-if="!isPrescription" class="col-teeth-amount text-end fw-medium">{{ getTeethWiseBillAmount(item) }}</td>
                                <td v-if="!isPrescription" class="col-status" v-html="getStatus(item.status)"></td>
                            </tr>
                            <ServiceTask
                                v-if="item.items && item.items.length > 0 && !isPrescription"
                                :childItems="item.items"
                                :colspan="visibleColumnCount"
                                @onOpenEditModal="(childItem, childIndex) => onOpenEditModal(childItem, noteIndex, childIndex, item)"
                                @onClickToRx="onClickToRx"
                            />
                        </template>

                        <tr v-if="!note.selectedNotes || !note.selectedNotes.length">
                            <td :colspan="visibleColumnCount" class="text-center text-muted py-4">No data</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="!isPrescription" class="col-12 bill-footer">
            <div class="bill-subtotal">
                <span class="bill-subtotal-label">Sub-Total</span>
                <span class="bill-subtotal-value">{{ billSubTotal }}</span>
            </div>
            <button
                @click="createBill"
                type="button"
                class="btn btn-primary"
                :disabled="loading || !hasBillableItems"
            >
                <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                Save
            </button>
        </div>

        <AddTreatmentPlan
            :selectedNotes="note.selectedNotes"
            v-model:isOpenModal="isOpenModal"
            v-if="isOpenModal"
            :dentition="dentition"
            :dentitionType="dentitionType"
            @onAddNewTask="onAddNewTask"
        />
        <TaskModal
            v-if="store.state.isModalOpenFour"
            :selectedTask="selectedTask"
            @onSaveTask="onSaveTask"
            :editData="editData"
        />
        <BillDentitionModal
            v-if="billDentitionItem"
            :singleItem="billDentitionItem"
            @onSave="onSaveBillDentition"
        />
    </div>
</template>

<script setup>
import {computed, inject, ref, watch} from "vue"
import { convertToTitleCase } from '@/services/utils/global';
import AddTreatmentPlan from './AddTreatmentPlan.vue';
import { formatDate } from '@/services/utils/global.js';
import {useRoute, useRouter} from "vue-router";
import cloneDeep from "lodash/cloneDeep";
import ServiceTask from './ServiceTask.vue';
import { useStore } from "vuex";
import TaskModal from './TaskModal.vue';
import BillDentitionModal from './BillDentitionModal.vue';
import useDate from '@/services/utils/day';
import handlePurchase from '@/services/modules/purchase';
import handleHospital from '@/services/modules/hospital';

const props = defineProps({
    note: {
        type: Object,
        default: () => {}
    },
    index: {
        type: Number,
        default: -1,
    },
    salesOrderId: {
        type: Number || null,
        default: null,
    },
    generalId: {
        type: Number || null,
        default: null,
    },
    dentition: {
        type: Object || null,
        default: () => {}
    },
    dentitionType: {
        type: String || null,
        default: null,
    },
    clientId: {
        type: Number || null,
        default: null,
    },
    from: {
        type: String,
        default: ''
    }
})

const route = useRoute();
const router = useRouter();
const generalId = computed(() => route.query.general_id);
const store = useStore();
const dateTime = useDate();
const isPrescription = computed(() => props.from === 'prescription');
const visibleColumnCount = computed(() => isPrescription.value ? 3 : 9);

const showSuccess = inject('showSuccess');
const showError = inject('showError');
const { storeBillPayment, fetchAccountReceivable, fetchVatPayable } = handlePurchase();
const { fetchSingleNote } = handleHospital();

const emit = defineEmits(['openModal']);
const isOpenModal = ref(false);
const selectedTask = ref(null);
const editData = ref(null);
const loading = ref(false);
const billDentitionItem = ref(null);

const billableItems = computed(() => {
    if(! props.note || ! props.note.selectedNotes) return [];
    return props.note.selectedNotes;
})

const newItems = computed(() => billableItems.value.filter(item => ! isBilled(item)));

const hasBillableItems = computed(() => billableItems.value.length > 0);

const billSubTotal = computed(() => {
    return billableItems.value.reduce((sum, item) => sum + getTeethWiseBillAmount(item), 0);
})

const onOpenModal = (note, index = -1) => {
    if(note.value){
        note.name = convertToTitleCase(note.value);
    }
    isOpenModal.value = true
}

const onOpenEditModal = (item, index, childIndex, parentItem) => {
    editData.value = item;
    editData.value.index = index;
    editData.value.childIndex = childIndex;
    selectedTask.value = parentItem;
    selectedTask.value.client_id = props.clientId;
    store.state.isModalOpenFour = true;
}

const onSaveTask = (item) => {
    const noteItem = props.note.selectedNotes.find(note => note.id === item.activitylogable_id);
    if(! noteItem) return;

    const taskIndex = noteItem.items.findIndex(task => task.id === item.id);
    if(taskIndex !== -1) {
        noteItem.items[taskIndex] = item;
        return;
    }
    noteItem.items.push(item);
}

const onOpenTaskModal = (item) => {
    editData.value = null;
    selectedTask.value = item;
    selectedTask.value.client_id = props.clientId;
    store.state.isModalOpenFour = true;
}

const onClickToRx = (item) => {
    const query = {
        ...cloneDeep(route.query),
        general_id: item.id,
        back_url: encodeURIComponent(route.fullPath)
    };
    if (item.task_id) query.task_id = item.task_id;
    const resolved = router.resolve({
        name: 'dental-service',
        params: {
            ...route.params,
            serviceId: route.params.serviceId
        },
        query: query,
        hash: '#prescription'
    });
    window.open(resolved.href, '_blank');
}

const onAddNewTask = () => {
    const submitFrom = isPrescription.value ? 'prescription' : 'treatment_plan';
    store.state.dental.serviceRef && store.state.dental.serviceRef.submit(false, submitFrom);
}

const getStatus = (status) => {
    if(! status) return '';
    const badgeStyle = 'display:inline-flex;align-items:center;justify-content:center;gap:4px;width:90px;height:24px;font-size:0.7rem;font-weight:600;line-height:1;';
    if(status === 'pending') {
        return `<span class="badge bg-warning" style="${badgeStyle}">Ongoing</span>`;
    }
    if(status === 'completed') {
        return `<span class="badge bg-success" style="${badgeStyle}"><i class="fas fa-check"></i> Completed</span>`;
    }
}

const getDentitionPertains = (dentitionPertains) => {
    if (!dentitionPertains || dentitionPertains.length < 0) return '';
    return dentitionPertains.filter(dentition => dentition.is_billed).map(dentition => dentition.tooth_number).join(', ').toUpperCase();
}

const isBilled = (item) => {
    return (item.invoice_qty || 0) > 0;
}

const hasTeethOption = (item) => {
    return item.dentition_pertains && item.dentition_pertains.length > 0;
}

const getBilledTeethCount = (item) => {
    if(! hasTeethOption(item)) return 0;
    return item.dentition_pertains.filter(dentition => dentition.is_billed).length;
}

const getInvoicedBillAmount = (item) => {
    const rate = item.rate || 0;
    const discount = item.discount_amount || 0;
    return (rate * (item.invoice_qty || 0)) - discount;
}

const getFullBillAmount = (item) => {
    const quantity = item.quantity || 0;
    const rate = item.rate || 0;
    const discount = item.discount_amount || 0;
    const amount = (rate * quantity) - discount;
    if(amount) return amount;
    return Number(item.sub_total || item.total || 0);
}

const getTeethWiseBillAmount = (item) => {
    if(isBilled(item)) return getInvoicedBillAmount(item);
    if(! hasTeethOption(item)) return getFullBillAmount(item);
    const rate = item.rate || 0;
    return rate * getBilledTeethCount(item);
}

const onOpenBillDentition = (item) => {
    billDentitionItem.value = item;
    store.state.isModalOpen = true;
}

const onSaveBillDentition = () => {
    billDentitionItem.value = null;
}

watch(() => store.state.isModalOpen, (isOpen) => {
    if(isOpen) return;
    billDentitionItem.value = null;
})

const getBillQuantity = (item) => {
    if(! hasTeethOption(item)) return item.quantity || 1;
    return getBilledTeethCount(item);
}

const buildItemDetails = () => {
    return newItems.value.map(item => {
        const rate = item.rate || 0;
        const quantity = getBillQuantity(item);
        const amount = getTeethWiseBillAmount(item);
        return {
            product_id: item.product_id,
            name: item.name,
            description: item.description || '',
            quantity: quantity,
            rate: rate,
            discount_percent: item.discount_percent || 0,
            discount_amount: item.discount_amount || 0,
            vat: item.vat || 0,
            vat_amount: item.vat_amount || 0,
            sub_total: amount,
            total: amount,
            contact_profile_id: item.service_resource ? item.service_resource.id : null,
            dentition_pertains: item.dentition_pertains || []
        }
    })
}

const fetchBillHeads = async () => {
    const companyQuery = `?company_id=${route.params.companyId}`;
    const ipdRegisterId = route.query.ipd_register_id;
    const heads = {
        account_head_id: null,
        vat_payable_account_head_id: null,
        ipd_register_id: null,
        service_resource_id: null
    };

    const [receivableRes, vatRes, noteRes] = await Promise.all([
        fetchAccountReceivable(companyQuery),
        fetchVatPayable(companyQuery),
        fetchSingleNote(companyQuery, ipdRegisterId)
    ]);

    if(receivableRes.data && receivableRes.data.length) heads.account_head_id = receivableRes.data[0].id;
    if(vatRes.data && vatRes.data.length) heads.vat_payable_account_head_id = vatRes.data[0].id;

    if(noteRes.data) {
        heads.ipd_register_id = noteRes.data.id;
        heads.service_resource_id = noteRes.data.human_resource_id;
    }

    return heads;
}

const buildFormData = (heads) => {
    return {
        company_id: route.params.companyId,
        contact_profile_id: props.clientId,
        ipd_register_id: heads.ipd_register_id,
        service_resource_id: heads.service_resource_id,
        account_head_id: heads.account_head_id,
        vat_payable_account_head_id: heads.vat_payable_account_head_id,
        sales_order_master_id: route.query.order_master_id || null,
        sale_type: 'ipd_invoice',
        receipt_type: 'ipd_invoice',
        status: 'active',
        has_item_detail: true,
        adjustment_amount: 0,
        service_charge_amount: 0,
        payment: 0,
        date: dateTime.currentDate(),
        description: '',
        item_details: buildItemDetails()
    }
}

const createBill = async () => {
    if(! newItems.value.length) return showError('No billable items in treatment plan');

    loading.value = true;

    try {
        const heads = await fetchBillHeads();
        const formData = buildFormData(heads);
        const res = await storeBillPayment(formData);

        if(! res.status) return showError(res.message);

        showSuccess(res.message);
        store.commit('dental/incrementReRenderCount');
    } catch (err) {
        showError('Something went wrong, please try again');
    } finally {
        loading.value = false;
    }
}

</script>

<style scoped>
/* ── Header ── */
.note-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0;
    margin-bottom: 0;
    padding: 10px 14px;
}
.note-title {
    min-width: 160px;
}
.note-title h4 {
    font-weight: 600;
    font-size: 15px;
    margin: 0;
    color: #5e5873;
}

/* ── Scroll wrapper ── */
.table-container {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}
.is-prescription .table-container {
    border: none;
    border-top: 1px solid #f1f0f5;
    border-radius: 0;
}
.is-prescription hr {
    display: none;
}
.is-prescription .treatment-table thead th {
    background-color: #fcfcfd;
    border-left: none;
    border-right: none;
    border-top: none;
    border-bottom: 1px solid #f1f0f5;
    font-size: 11px;
}
.is-prescription .treatment-table tbody td {
    border-left: none;
    border-right: none;
    border-color: #f1f0f5;
    font-size: 13px;
}

/* ── Table base ── */
.treatment-table {
    table-layout: fixed;
    width: 100%;
    min-width: 820px;       /* scroll before crushing */
    border-collapse: collapse;
}

.treatment-table.is-compact {
    table-layout: auto;
    min-width: 0;
}

.treatment-table.is-compact .col-item    { width: 60%; }
.treatment-table.is-compact .col-dentition {
    width: 40%;
    max-width: 0;
}

.treatment-table.is-compact thead th,
.treatment-table.is-compact tbody td {
    padding: 0.35rem 0.75rem;
}

/* ── Header cells ── */
.treatment-table thead th {
    background-color: #f8f9fa;
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.65rem 0.6rem;
    border: 1px solid #e5e7eb;
    border-bottom-width: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
}

/* ── Body cells ── */
.treatment-table tbody td {
    padding: 0.55rem 0.6rem;
    vertical-align: middle;
    border: 1px solid #e5e7eb;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #374151;
}

.treatment-table tbody tr:hover td {
    background-color: #f5f7ff;
}

.treatment-table .child-td {
    padding: 0 !important;
    border: 1px solid #e5e7eb;
}

/* ── Highlighted row (generalId match) ── */
.row-highlighted td {
    background-color: #e6f8ee !important;
    color: #287d54 !important;
    font-weight: 500;
}

/* ── Column widths + alignment (applies to th + td) ── */
.treatment-table .col-action       { width: 10%;  text-align: center; }
.treatment-table .col-item         { width: 17%; text-align: left; }
.treatment-table .col-doctor       { width: 11%; text-align: left; }
.treatment-table .col-schedule     { width: 12%; text-align: left; }
.treatment-table .col-dentition    { width: 9%; text-align: left; }
.treatment-table .col-bill-dent    { width: 12%; text-align: left; }
.treatment-table .col-amount       { width: 9%;  text-align: right; }
.treatment-table .col-teeth-amount { width: 12%; text-align: right; }
.treatment-table .col-status       { width: 8%;  text-align: center; }

/* ── Task button ── */
.task-btn {
    padding: 0.2rem 0.5rem;
    white-space: nowrap;
}

/* ── Tooth button ── */
.tooth-btn {
    padding: 0.15rem 0.4rem;
    color: #7367f0;
}
.tooth-btn:hover {
    color: #5e50ee;
}

/* ── Bill footer ── */
.bill-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 1.25rem;
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid #e5e7eb;
}
.bill-subtotal {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
}
.bill-subtotal-label {
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.85rem;
}
.bill-subtotal-value {
    font-weight: 700;
    font-size: 1.15rem;
    color: #111827;
}
</style>